import { useEffect, useState, useTransition, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Brain,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  ExternalLink,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Loader2,
  Lock,
  Settings,
  Shield,
  Sparkles,
  Video,
} from "lucide-react";

import visionLogo from "@/assets/Logo_Transparent_White.png";
import { BrandSwitcherPopover } from "@/components/layout/BrandSwitcherPopover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CORE_ROUTES } from "@/config/coreRoutes";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { checkSidebarIntegrity, extractSidebarPaths } from "@/lib/navigationIntegrity";
import { cn } from "@/lib/utils";

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  children?: {
    name: string;
    href: string;
    icon?: React.ElementType;
    external?: boolean;
    tierOnly?: "reseller";
  }[];
  adminOnly?: boolean;
  hideForAdmin?: boolean;
  hidden?: boolean;
  paidOnly?: boolean;
  /** Solopreneur-only (DB tier "reseller"): shows a lock for Free + Starter but
      keeps the item visible & clickable → lands on the /coaching upgrade wall. */
  soloOnly?: boolean;
  paidOrAdminOnly?: boolean;
  dataTour?: string;
  external?: boolean;
  externalUrl?: string;
}

// ── Redesigned navigation (new-ui prototype) ──
// Three groups: "For this brand" (brand-scoped tools) · "Your business" ·
// "Account". Paths still come from CORE_ROUTES (single source of truth).
//
// VA-142 — Bridget's video feedback (2026-07-27): "I don't really want any more
// tabs over here for the brand. Everything is just going to be run here through
// the assistant." So this section is exactly TWO items, matching the prototype:
// The Brain + Assistant.
//
// "Analyze Brand" and "Weekly" are hidden rather than deleted: `hidden` keeps the
// route alive (see docs/rules/routing-sidebar.md) so the Assistant Launchpad can
// still launch them and no feature is orphaned. Neither path is in
// REQUIRED_SIDEBAR_PATHS, so the dev integrity guard stays quiet.
const brandNavigation: NavItem[] = [
  {
    name: "The Brain",
    href: CORE_ROUTES.brandGuide.path,
    icon: Brain,
    dataTour: "brand-guide",
  },
  {
    name: "Assistant",
    href: CORE_ROUTES.visionAssistant.path,
    icon: Sparkles,
    dataTour: "vision-assistant",
    paidOnly: true,
  },
  {
    // Launched from the Assistant Launchpad ("Analyze Brand agent"), not the nav.
    name: "Analyze Brand",
    href: CORE_ROUTES.marketingAudit.path,
    icon: BarChart3,
    dataTour: "marketing-audit",
    hidden: true,
  },
  {
    // Launched from the Assistant Launchpad; route kept for direct links.
    name: "Weekly",
    href: CORE_ROUTES.topics.path,
    icon: CalendarDays,
    dataTour: "weekly",
    paidOnly: true,
    hidden: true,
  },
];

const businessNavigation: NavItem[] = [
  {
    name: CORE_ROUTES.dashboard.label,
    href: CORE_ROUTES.dashboard.path,
    icon: LayoutDashboard,
    dataTour: "dashboard",
  },
  {
    name: CORE_ROUTES.coaching.label,
    href: CORE_ROUTES.coaching.path,
    icon: Video,
    soloOnly: true,
  },
  {
    name: CORE_ROUTES.services.label,
    href: CORE_ROUTES.services.path,
    icon: Briefcase,
  },
  {
    name: "Content History",
    href: CORE_ROUTES.history.path,
    icon: Clock,
  },
  {
    name: CORE_ROUTES.support.label,
    href: CORE_ROUTES.support.path,
    icon: LifeBuoy,
    // Support is open to ALL plans (Free & Starter get General Questions + Report a Bug;
    // per-category gating like "My Client Support" is enforced inside NewTicketDialog).
  },
];

const accountNavigation: NavItem[] = [
  {
    // The prototype's Agency area. Solopreneur-only: it is client-delivery
    // tooling for people managing brands on behalf of someone else, which is
    // exactly what that plan is. soloOnly (not tierOnly) so lower plans still
    // see it with a lock and land on the upgrade wall, rather than it silently
    // not existing — see docs/rules/routing-sidebar.md.
    name: CORE_ROUTES.agency.label,
    href: CORE_ROUTES.agency.path,
    icon: Building2,
    soloOnly: true,
  },
  {
    name: CORE_ROUTES.admin.label,
    href: CORE_ROUTES.admin.path,
    icon: Shield,
    adminOnly: true,
  },
  {
    name: "My Plan",
    href: CORE_ROUTES.plan.path,
    icon: CreditCard,
  },
  {
    name: CORE_ROUTES.settings.label,
    href: CORE_ROUTES.settings.path,
    icon: Settings,
  },
];

// Group filter — preserves access control (admin-only, paid-or-admin-only).
// paidOnly items are NOT filtered out; they render with a lock icon instead.
function isNavVisible(nav: NavItem, isAdmin: boolean, isFreeUser: boolean) {
  if (nav.hidden) return false;
  if (nav.adminOnly && !isAdmin) return false;
  if (nav.hideForAdmin && isAdmin) return false;
  if (nav.paidOrAdminOnly && isFreeUser && !isAdmin) return false;
  return true;
}

if (import.meta.env.DEV) {
  const allPaths = extractSidebarPaths(
    [...brandNavigation, ...businessNavigation],
    accountNavigation
  );
  checkSidebarIntegrity(allPaths);
}

const itemCls =
  "group relative flex w-full select-none items-center gap-2.5 rounded-[10px] border border-transparent px-2.5 py-2.5 text-[13px] font-medium outline-none transition-all duration-150";

const collapsedItemCls =
  "mx-auto h-10 w-10 justify-center gap-0 px-0 py-0 text-[13px]";

// Active item (redesign): translucent white fill + gold text/border (#ffb758).
const activeItemCls =
  "border-brand-gold/30 bg-white/[0.16] text-brand-gold";

const inactiveItemCls =
  "text-white/[0.88] hover:bg-white/[0.08] hover:text-white";

const childActiveItemCls =
  "border-sidebar-accent/25 bg-sidebar-accent/[0.15] text-sidebar-foreground";

function ActivePip() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-y-[18%] left-0 w-[3px] rounded-r-full bg-brand-gold shadow-[0_0_6px_1px_rgba(255,183,88,0.35)]"
    />
  );
}

function SectionLabel({
  children,
  icon: Icon,
}: {
  children: React.ReactNode;
  icon?: React.ElementType;
}) {
  return (
    <div className="flex items-center gap-2 px-3 pb-1.5 pt-5">
      {Icon && <Icon className="h-3 w-3 shrink-0 text-sidebar-foreground/40" />}
      <span className="select-none whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/40">
        {children}
      </span>
      <span className="h-px flex-1 bg-sidebar-border/20" />
    </div>
  );
}

function WithTooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="right"
        className="border-border bg-card text-xs text-card-foreground"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarSkeleton({ collapsed }: { collapsed: boolean }) {
  return (
    <aside
      className={cn(
        "flex h-[100dvh] flex-col border-r border-sidebar-border/15 bg-sidebar transition-all duration-300",
        collapsed ? "w-16" : "w-60"
      )}
    >
      <div className="flex h-[60px] shrink-0 items-center border-b border-sidebar-border/15 px-4">
        <Skeleton className="h-8 w-28 rounded-md bg-sidebar-muted/25" />
      </div>

      {!collapsed && (
        <div className="mx-3 mt-3">
          <Skeleton className="h-[44px] w-full rounded-lg bg-sidebar-muted/20" />
        </div>
      )}

      <div className="flex-1 space-y-1.5 px-2 pt-4">
        {[1, 2, 3, 4, 5].map((item) => (
          <Skeleton
            key={item}
            className="h-10 w-full rounded-xl bg-sidebar-muted/15"
          />
        ))}
      </div>

      <div className="space-y-1.5 border-t border-sidebar-border/15 px-2 py-3">
        {[1, 2, 3].map((item) => (
          <Skeleton
            key={item}
            className="h-10 w-full rounded-xl bg-sidebar-muted/15"
          />
        ))}
      </div>
    </aside>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );
  const [expandedMenus, setExpandedMenus] = useState<string[]>(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? [] : ["Workflow"]
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);

  // VA-243: which item is waiting for its page to arrive.
  //
  // Every route in this app is code-split, and the navigation below runs inside a transition.
  // A transition deliberately suppresses the Suspense fallback and keeps the CURRENT page on
  // screen until the new chunk has downloaded — which is the right call visually, but it means
  // that on a slow connection a sidebar click produced no spinner, no skeleton, and no change
  // of any kind. Clarity recorded it as exactly what it looked like: a dead click. "The Brain"
  // and "Admin Panel" both show up in that list, and both are heavy pages.
  const [isPending, startTransition] = useTransition();
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { credits } = useCredits();
  const isFreeUser = credits?.subscription_tier !== "solopreneur" && credits?.subscription_tier !== "reseller";
  // Solopreneur-only lock (DB "reseller" === Solopreneur plan). Locks Free + Starter.
  const isNonSolo = credits?.subscription_tier !== "reseller";

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(collapsed));
  }, [collapsed]);

  // The page has arrived, so stop saying it is on its way. Keyed on the path rather than on
  // isPending alone: a transition that is cancelled or re-targeted would otherwise leave the
  // spinner running on an item nobody is waiting for.
  useEffect(() => {
    setPendingHref(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobile && collapsed) {
      setCollapsed(false);
    }
  }, [collapsed, isMobile]);

  useEffect(() => {
    async function checkAdminStatus() {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      setIsAdmin(!!data);
    }

    void checkAdminStatus();
  }, [user]);

  // Admin only: count of UNREAD support tickets (active tickets the admin hasn't
  // opened yet), shown as a notification badge on the Support nav item.
  // Updates live when a ticket is submitted or marked read.
  useEffect(() => {
    if (!isAdmin) {
      setOpenTicketCount(0);
      return;
    }

    async function fetchUnreadCount() {
      const { data, error } = await supabase.rpc("get_unread_support_count");
      if (!error) setOpenTicketCount((data as number | null) ?? 0);
    }

    void fetchUnreadCount();

    const channel = supabase
      .channel("sidebar-support-tickets")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_tickets" },
        () => { void fetchUnreadCount(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_ticket_reads" },
        () => { void fetchUnreadCount(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [isAdmin]);

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((entry) => entry !== name) : [...prev, name]
    );
  };

  const isChildActive = (nav: NavItem) =>
    nav.children?.some((child) => location.pathname === child.href);

  const handleSidebarNavigation = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    setPendingHref(href);
    startTransition(() => {
      navigate(href);
    });
  };

  if (!user) {
    return <SidebarSkeleton collapsed={collapsed} />;
  }

  const renderNav = (nav: NavItem) => {
    const isActive = nav.href ? location.pathname === nav.href : false;
    const hasChildren = Boolean(nav.children?.length);
    const isExpanded = expandedMenus.includes(nav.name);
    const childActive = isChildActive(nav);
    const isLocked = Boolean((nav.paidOnly && isFreeUser) || (nav.soloOnly && isNonSolo));
    const badgeCount = nav.href === CORE_ROUTES.support.path ? openTicketCount : 0;
    const isNavigating = isPending && !!nav.href && pendingHref === nav.href;

    if (hasChildren) {
      const trigger = (
        <div key={nav.name} data-tour={nav.dataTour}>
          <button
            onClick={() => toggleMenu(nav.name)}
            className={cn(
              itemCls,
              collapsed && collapsedItemCls,
              childActive ? activeItemCls : inactiveItemCls
            )}
          >
            {childActive && <ActivePip />}

            <nav.icon
              className={cn(
                "h-4 w-4 shrink-0 transition-colors",
                childActive
                  ? "text-brand-gold"
                  : "text-white/70 group-hover:text-white"
              )}
            />

            {!collapsed && (
              <>
                <span className="flex-1 text-left leading-none">{nav.name}</span>

                {isLocked ? (
                  <Lock className="h-3.5 w-3.5 shrink-0 text-brand-gold" />
                ) : (
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 text-sidebar-foreground/35 transition-transform duration-200",
                      isExpanded && "rotate-180"
                    )}
                  />
                )}
              </>
            )}
          </button>

          {!collapsed && (
            <div
              className={cn(
                "overflow-hidden transition-all duration-200 ease-in-out",
                isExpanded ? "mt-0.5 max-h-64 opacity-100" : "max-h-0 opacity-0"
              )}
            >
              <div className="ml-[22px] space-y-0.5 border-l border-sidebar-border/20 py-0.5 pl-3">
                {nav.children
                  ?.filter(
                    (child) =>
                      !child.tierOnly || child.tierOnly === credits?.subscription_tier
                  )
                  .map((child) => {
                    const childActive = location.pathname === child.href;
                    const childTour =
                      child.href.includes("pre-live")
                        ? "pre-live"
                        : child.href.includes("post-live")
                          ? "post-live"
                          : undefined;

                    if (child.external) {
                      return (
                        <a
                          key={child.name}
                          href={child.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(itemCls, inactiveItemCls, "py-2 text-[12.5px]")}
                        >
                          {child.icon && (
                            <child.icon className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40" />
                          )}
                          <span className="flex-1">{child.name}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-sidebar-foreground/25" />
                        </a>
                      );
                    }

                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        onClick={(event) => handleSidebarNavigation(event, child.href)}
                        data-tour={childTour}
                        className={cn(
                          itemCls,
                          childActive ? childActiveItemCls : inactiveItemCls,
                          "py-2 text-[12.5px]"
                        )}
                      >
                        {childActive && <ActivePip />}
                        {isPending && pendingHref === child.href ? (
                          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-sidebar-accent" />
                        ) : child.icon ? (
                          <child.icon
                            className={cn(
                              "h-3.5 w-3.5 shrink-0",
                              childActive
                                ? "text-sidebar-accent"
                                : "text-sidebar-foreground/40 group-hover:text-sidebar-foreground/65"
                            )}
                          />
                        ) : (
                          <span
                            className={cn(
                              "h-1.5 w-1.5 shrink-0 rounded-full",
                              childActive
                                ? "bg-sidebar-accent"
                                : "bg-sidebar-foreground/30"
                            )}
                          />
                        )}
                        <span>{child.name}</span>
                      </Link>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      );

      return collapsed ? <WithTooltip key={nav.name} label={nav.name}>{trigger}</WithTooltip> : trigger;
    }

    if (nav.external && nav.externalUrl) {
      const element = (
        <a
          key={nav.name}
          href={nav.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            event.preventDefault();
            window.open(nav.externalUrl, "_blank", "noopener,noreferrer");
          }}
          data-tour={nav.dataTour}
          className={cn(itemCls, collapsed && collapsedItemCls, inactiveItemCls)}
        >
          <nav.icon className="h-4 w-4 shrink-0 text-sidebar-foreground/45 transition-colors group-hover:text-sidebar-foreground/75" />
          {!collapsed && (
            <>
              <span className="flex-1">{nav.name}</span>
              <ExternalLink className="h-3 w-3 shrink-0 text-sidebar-foreground/25" />
            </>
          )}
        </a>
      );

      return collapsed ? <WithTooltip key={nav.name} label={nav.name}>{element}</WithTooltip> : element;
    }

    const element = (
      <Link
        key={nav.name}
        to={nav.href!}
        onClick={(event) => handleSidebarNavigation(event, nav.href!)}
        data-tour={nav.dataTour}
        aria-busy={isNavigating || undefined}
        className={cn(
          itemCls,
          collapsed && collapsedItemCls,
          isActive ? activeItemCls : inactiveItemCls
        )}
      >
        {isActive && <ActivePip />}

        <span className="relative shrink-0">
          {isNavigating ? (
            <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
          ) : (
            <nav.icon
              className={cn(
                "h-4 w-4 transition-colors",
                isActive
                  ? "text-brand-gold"
                  : "text-white/70 group-hover:text-white"
              )}
            />
          )}
          {/* Collapsed sidebar: red dot on the icon when there are open tickets */}
          {collapsed && badgeCount > 0 && (
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-brand-gold ring-1 ring-brand-deep-purple" />
          )}
        </span>

        {!collapsed && (
          <>
            <span className="flex-1 leading-none">{nav.name}</span>
            {/* Kate, VA-181: this badge was raw #ef4444, which is off-brand. Red is
                reserved for destructive actions and LIVE indicators
                (docs/rules/brand-colors.md) — an unread count is neither. Gold is the
                palette's attention color, with dark text since gold needs it for
                contrast. */}
            {badgeCount > 0 && (
              <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-gold px-1.5 text-[11px] font-semibold leading-none text-brand-deep-purple">
                {badgeCount > 99 ? "99+" : badgeCount}
              </span>
            )}
            {isLocked && (
              <Lock className="h-3.5 w-3.5 shrink-0 text-brand-gold" />
            )}
          </>
        )}
      </Link>
    );

    return collapsed ? <WithTooltip key={nav.name} label={nav.name}>{element}</WithTooltip> : element;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-tour="sidebar"
        className={cn(
          "scrollbar-none flex h-[100dvh] flex-col overflow-x-hidden overflow-y-auto border-r border-white/[0.06] bg-[linear-gradient(180deg,#7a1d92_0%,#5a0c70_32%,#48075b_60%,#2c0338_100%)] transition-all duration-300 ease-in-out",
          collapsed ? "w-16" : "w-60"
        )}
      >
        <div
          className={cn(
            "flex shrink-0 items-center border-b border-white/[0.06]",
            collapsed ? "h-[68px]" : "h-[72px]",
            collapsed ? "justify-center px-2" : "justify-center px-4"
          )}
        >
          <Link to="/" className="flex items-center">
            <img
              src={visionLogo}
              alt="My Vision Assistant"
              className={cn(
                "w-auto object-contain transition-all duration-200",
                collapsed ? "h-11 max-w-12" : "h-[72px] max-w-[158px]"
              )}
            />
          </Link>
        </div>

        {!collapsed && <BrandSwitcherPopover collapsed={false} />}

        <nav
          aria-label="Main navigation"
          className="flex-1 shrink-0 overflow-visible px-2 pb-2"
        >
          {/* For this brand */}
          {!collapsed && <SectionLabel icon={Layers}>For this brand</SectionLabel>}
          <div className={cn("space-y-0.5", collapsed ? "pt-3" : "pt-1")}>
            {brandNavigation
              .filter((nav) => isNavVisible(nav, isAdmin, isFreeUser))
              .map(renderNav)}
          </div>

          {/* Your business */}
          {!collapsed && <SectionLabel>Your business</SectionLabel>}
          {collapsed && <div className="my-2 h-px bg-white/[0.06]" />}
          <div className="space-y-0.5">
            {businessNavigation
              .filter((nav) => isNavVisible(nav, isAdmin, isFreeUser))
              .map(renderNav)}
          </div>
        </nav>

        <div
          aria-label="Account navigation"
          className={cn(
            "shrink-0 border-t border-white/[0.06] px-2",
            "pb-[calc(env(safe-area-inset-bottom)+6px)] lg:pb-2"
          )}
        >
          {!collapsed && <SectionLabel>Account</SectionLabel>}
          {collapsed && <div className="h-3" />}

          <div className="space-y-0.5">
            {accountNavigation
              .filter((nav) => isNavVisible(nav, isAdmin, isFreeUser))
              .map(renderNav)}
          </div>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setCollapsed((prev) => !prev)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className={cn(
                "hidden h-10 w-full shrink-0 items-center justify-center border-t border-white/[0.06] text-sidebar-foreground/45 transition-colors duration-200 hover:bg-white/[0.06] hover:text-sidebar-foreground lg:flex"
              )}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className="border-border bg-card text-card-foreground"
          >
            {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          </TooltipContent>
        </Tooltip>
      </aside>
    </TooltipProvider>
  );
}
